import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause } from '@fortawesome/free-solid-svg-icons';
import qaData from '../assets/qa.json';
import javaData from '../assets/java.json';
import databaseData from '../assets/database.json';
import dotnetData from '../assets/dotnet.json';
/* import { mockAssessmentResult } from '../mockAssessmentResult.js'; */
import CustomMarkdownRenderer from '../components/CustomMarkdownRenderer.js';
import mammoth from 'mammoth';

function LiveAssessment() {
  const [selectedSkill, setSelectedSkill] = useState('');
  const [jdFile, setJdFile] = useState(null);
  const [jdContent, setJdContent] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const audioChunks = useRef([]);
  const dropdownRef = useRef(null); // Declare dropdownRef here

  const skills = ['QA', 'Java', 'Database', '.NET'];

  const handleSkillSelect = (skill) => {
    setSelectedSkill(skill);
    setShowDropdown(false);
  };

  const validFileTypes = ['text/plain', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

  const handleFileChange = (event, setFile, setContent) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!validFileTypes.includes(file.type)) {
      setErrorMessage('Please upload a file in .txt or .docx format.');
      setFile(null);
      setContent('');
      return;
    }

    setErrorMessage('');
    setFile(file);

    const reader = file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ? mammoth.extractRawText({ arrayBuffer: file })
        .then(result => setContent(result.value))
        .catch(err => console.error('Error reading file:', err))
      : new FileReader();

    if (reader instanceof FileReader) {
      reader.onload = (e) => setContent(e.target.result);
      reader.readAsText(file);
    }
  };

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream);

    recorder.start();
    setIsRecording(true);
    setMediaRecorder(recorder);
    audioChunks.current = []; // Reset audioChunks on new recording

    recorder.ondataavailable = (event) => {
      audioChunks.current.push(event.data);
    };

    recorder.onstop = async () => {
      const audioBlob = new Blob(audioChunks.current, { type: 'audio/wav' });
      const generatedTranscript = await getTranscriptFromBlob(audioBlob);
      setTranscript(generatedTranscript);
      // Log the transcript to the console
      console.log('Transcript:', generatedTranscript);
      setIsAssessButtonEnabled(!!generatedTranscript); // Enable button if transcript exists
    };
  };

  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const getTranscriptFromBlob = async (audioBlob) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve("This is a Mock, integrate an API like Google Cloud Speech-to-Text that can process audio files and return the transcript.");
      }, 1000);
    });
  };

  const getSkillData = (skill) => {
    switch (skill) {
      case 'QA':
        return qaData;
      case 'Java':
        return javaData;
      case 'Database':
        return databaseData;
      case '.NET':
        return dotnetData;
      default:
        return null;
    }
  };

  const handleAssessClick = async () => {
    const dataToSend = {
      jdContent,
      skillData: getSkillData(selectedSkill),
      transcript,
    };

    setTimeout(() => {
      setAssessmentResult(mockAssessmentResult);
    }, 1000);
  };

  const handleReset = () => {
    setSelectedSkill('');
    setJdFile(null);
    setJdContent('');
    setErrorMessage('');
    setTranscript('');
    setAssessmentResult(null);
    setIsAssessButtonEnabled(false);
  };

  const [isAssessButtonEnabled, setIsAssessButtonEnabled] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Enable the assess button only if both skill is selected and transcript exists
    setIsAssessButtonEnabled(!!selectedSkill && !!transcript);
  }, [selectedSkill, transcript]);

  return (
    <div>
      {errorMessage && <div className="error-message" style={{ color: 'red' }}>{errorMessage}</div>}

      <UploadSection
        title="Step 1. Click to Upload Job Description"
        file={jdFile}
        content={jdContent}
        onChange={(e) => handleFileChange(e, setJdFile, setJdContent)}
      />

      <div className="labels">
        <div className="upload-section" ref={dropdownRef}>
          <label>Step 2. Select Skill to Evaluate</label>
          <div className="custom-dropdown">
            <div className="dropdown-toggle" onClick={() => setShowDropdown(prev => !prev)}>
              {selectedSkill || 'Select Skill'}
            </div>
            <div className={`dropdown-menu ${showDropdown ? 'show' : ''}`}>
              {skills.map((skill) => (
                <div
                  key={skill}
                  className="dropdown-item"
                  onClick={() => handleSkillSelect(skill)}
                >
                  {skill}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="labels">
        <div className="upload-section">
          <label>Step 3: Click to Start/Stop Recording</label>
          <button
            onClick={isRecording ? stopRecording : startRecording}
            style={{
              marginTop: '10px',
              borderRadius: '50%',
              width: '60px',
              height: '60px',
              backgroundColor: isRecording ? '#ff4d4d' : '#4CAF50',
              border: 'none',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              cursor: 'pointer',
            }}
          >
            <FontAwesomeIcon icon={isRecording ? faPause : faPlay} />
          </button>
          {transcript && (
            <div>
              <h3>Transcript</h3>
              <p>{transcript}</p>
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: '0.9cm' }}>
        <div className="bottom-section">
          <button
            onClick={handleAssessClick}
            disabled={!isAssessButtonEnabled}
            style={{ backgroundColor: isAssessButtonEnabled ? '#4CAF50' : '#ccc', color: 'white' }}
          >
            Click to Assess
          </button>
          <button
            onClick={handleReset}
            style={{ marginLeft: '10px', backgroundColor: '#ff4d4d', color: 'white' }}
          >
            Reset
          </button>
        </div>
      </div>

      {assessmentResult && (
        <div className="assessment-results">
          <h2>Candidate Assessment: {assessmentResult.candidateName}</h2>
          <CustomMarkdownRenderer markdownContent={assessmentResult.content} />
        </div>
      )}
    </div>
  );
}

const UploadSection = ({ title, file, content, onChange }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="labels">
      <div className="upload-section">
        <label htmlFor="file-upload" className="button">{title}</label>
        <input
          id="file-upload"
          type="file"
          accept=".txt, .docx"
          onChange={onChange}
          style={{ display: 'none' }}
        />
        {file && (
          <div
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            style={{ position: 'relative', display: 'inline-block' }}
          >
            <p class="uploaded-jd">Uploaded File: {file.name}</p>
            {showTooltip && (
              <div className="tooltip">
                <p>{content.length > 100 ? content.slice(0, 100) + '...' : content}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveAssessment;
